import dayjs from 'dayjs';
import { isEmpty, isNotNil, last, sortBy } from 'rambda';
import { useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useRevalidator } from 'react-router';
import { type Components, GroupedVirtuoso, type GroupedVirtuosoHandle, type ListRange } from 'react-virtuoso';
import { useDebouncedCallback, useThrottledCallback } from 'use-debounce';
import { useTimeout } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';
import MessageListItem from '@/components/conversations/conversation-list/message-list-item/message-list-item';
import EmptyLayout from '@/components/layouts/empty-layout';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import useIosScrollByAdjustment from '@/hooks/conversations/use-ios-scrollby-adjustment';
import useMarkSessionRead from '@/hooks/conversations/use-mark-session-read';
import useMessageHistory from '@/hooks/conversations/use-message-history';
import useLastPosition from '@/hooks/sessions/use-last-position';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useUserPlatform from '@/hooks/use-user-platform';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import ROUTES from '@/routes';
import { getUserId } from '@/stores/auth';
import { useMessageStore } from '@/stores/message';
import DateHeader from './date-header';
import LoadingHeader from './loading-header';
import ScrollToEndButton from './scroll-to-end-button';
import ScrollToLastReadButton from './scroll-to-last-read-button';

interface ConversationListProps {
  showHeader?: boolean;
  isLoading: boolean;
  initialUnreadCount?: number;
  searchId?: number;
  excludeTypes?: Set<ConversationMessageContentType>;
}

const MAX_COUNT = Math.floor(Number.MAX_SAFE_INTEGER / 100);

export default function ConversationList({
  showHeader,
  isLoading,
  initialUnreadCount = 0,
  searchId,
  excludeTypes = new Set([ConversationMessageContentType.LeaveGroup]),
}: ConversationListProps) {
  const { revalidate } = useRevalidator();
  const listId = useId();
  const { isIOS, isDesktop } = useUserPlatform();
  const listRef = useRef<GroupedVirtuosoHandle>(null);
  const maxCount = useRef(MAX_COUNT);
  const [firstItemIndex, setFirstItemIndex] = useState(MAX_COUNT);
  const { type, id } = useConversationStore();

  const { hydrated: isHydrated } = useMessageStore();
  const messages = useMessageStore(useShallow((s) => sortBy((i) => i.ID, Array.from(s.messages.values()))));

  const [groupedDates, groupCounts] = useMemo(() => groupMessagesByDate(messages), [messages]);
  const {
    fetchPreviousPage,
    fetchNextPage,
    hasPreviousPage,
    hasNextPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
    isEnabled: fetchEnabled,
    setEnable: enableFetch,
  } = useMessageHistory(type, id, searchId, !searchId);
  const { lastPosition, setLastPosition, clearLastPosition } = useLastPosition(
    type === 'group'
      ? ROUTES.MESSAGES.GROUPS.$buildPath({ params: { id } })
      : ROUTES.MESSAGES.SESSIONS.$buildPath({ params: { id } }),
    !searchId,
  );

  const { markRead, markAllRead } = useMarkSessionRead();
  // use immediate and debounced value to delay the timing of scrollToEndButton showing
  const [atEdge, setAtEdge] = useState<'head' | 'tail' | null>('tail');
  const debouncedAtEdge = useDeferredValue(atEdge);

  const [unreadCount, setUnreadCount] = useState(() => initialUnreadCount);

  useEffect(() => {
    setUnreadCount(initialUnreadCount);
  }, [initialUnreadCount]);

  const unreadMessagesId = useMemo(
    () => (messages.length ? messages.at(messages.length - initialUnreadCount)?.ID : undefined),
    [messages, initialUnreadCount],
  );
  const unreadMessagesIndex = useMemo(
    () => messages.findIndex((message) => message.ID === unreadMessagesId),
    [messages, unreadMessagesId],
  );

  const { isKeyboardVisible } = useAdjustLayout({ inputTargetSelector: '#quill-editor .ql-editor' });
  useIosScrollByAdjustment(`virtuoso-list-${listId}`, !isEmpty(groupCounts));

  useTimeout(
    () => {
      if (isKeyboardVisible && atEdge === 'tail') scrollTo();
    },
    isKeyboardVisible && atEdge === 'tail' ? 100 : null,
  );

  useEffect(() => {
    if (isHydrated && !isLoading && (searchId || lastPosition)) {
      const timeoutId = setTimeout(() => {
        if (searchId) {
          scrollToMessageId(searchId, 'auto');
        } else if (lastPosition?.anchorId && !isDesktop) {
          scrollToMessageId(lastPosition.anchorId, 'auto');
          clearLastPosition();
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [isHydrated, isLoading, searchId]);

  const scrollTo = (index?: number, behavior: 'auto' | 'smooth' = 'smooth') => {
    listRef.current?.scrollToIndex({ index: index ?? 'LAST', behavior, offset: -64, align: 'start' });

    // NOTE: workaround for temporary blank screen on iOS
    if (isIOS)
      setTimeout(
        () => listRef.current?.scrollToIndex({ index: index ?? 'LAST', behavior, offset: -64, align: 'start' }),
        300,
      );
  };

  const scrollToMessageId = (id: number, behavior: 'auto' | 'smooth' = 'smooth') => {
    if (id <= 0) return;
    const index = messages.findIndex((message) => message.ID === id);
    scrollTo(index, behavior);
  };

  useEffect(() => {
    if (useMessageStore.getState().hydrated) {
      setFirstItemIndex(maxCount.current - useMessageStore.getState().messages.size);
    }

    return useMessageStore.subscribe(({ messages }, prevState) => {
      const messageCollection = Array.from(messages.keys());
      const prevMessageCollection = Array.from(prevState.messages.keys());
      const lengthDiff = messageCollection.length - prevMessageCollection.length;

      if (lengthDiff === 0) return;

      // Delete self messages
      maxCount.current += Math.min(lengthDiff, 0);

      if (last(messageCollection) !== last(prevMessageCollection) && messages.size - prevState.messages.size === 1) {
        // Received new message
        maxCount.current += 1;
      } else {
        // Load previous messages
        setFirstItemIndex(maxCount.current - messages.size);
      }
    });
  }, []);

  const renderItemContent = (index: number) => {
    const messageIndex = index - maxCount.current + messages.length;
    const message = messages.at(messageIndex);

    return !message || excludeTypes.has(message.MsgType) ? (
      <div
        key={index}
        id={`message-${messageIndex}`}
        className='h-px'
      />
    ) : (
      <MessageListItem
        key={index}
        type='clamped'
        message={message}
        isHighlighted={searchId === message.ID}
        onResize={() => listRef.current?.autoscrollToBottom()}
      />
    );
  };

  const debouncedStartReached = useThrottledCallback(async () => {
    if (isLoading || !fetchEnabled || !isHydrated || !hasPreviousPage || isFetchingPreviousPage) return;

    await fetchPreviousPage();
  }, 600);

  const debouncedEndReached = useDebouncedCallback(async () => {
    const endMessage = messages.at(-1);
    if (!endMessage) return;

    clearLastPosition();

    if (
      endMessage.ID !== endMessage.TimeStamp &&
      endMessage.Status !== null &&
      (type === 'group' || getUserId() !== endMessage.UserID)
    ) {
      markAllRead(type, id, endMessage.ID);
    }

    if (isLoading || !fetchEnabled || !isHydrated || !hasNextPage || isFetchingNextPage) return;

    await fetchNextPage();
  }, 600);

  const handleScrollToEdgeClick = (edge: 'head' | 'tail' = 'tail') => scrollTo(edge === 'head' ? 0 : undefined);
  const handleRangeChange = (range: ListRange) => {
    if (isLoading || !isHydrated) return;

    const ranges = Array.from({ length: range.endIndex - range.startIndex + 1 })
      .fill(0)
      .map((_, i) => {
        const index = i + range.startIndex - maxCount.current + messages.length;
        return messages.at(index);
      })
      .filter(isNotNil);

    if (unreadMessagesId !== undefined && ranges[0]?.ID && ranges[0].ID < unreadMessagesId) {
      setUnreadCount(0);
      revalidate().catch(console.warn);
    }

    for (const { ID, IsRead, Status, UserID } of ranges) {
      const userId = getUserId();

      if (Status === null || IsRead || UserID === userId) continue;

      markRead(type, id, ID);
    }

    const startMessage = ranges.at(0);

    if (startMessage) {
      setLastPosition(startMessage.ID);
    }
  };

  const components = {
    Header: () =>
      searchId && !fetchEnabled ? (
        <LoadingHeader
          hide={!hasPreviousPage}
          isLoading={isFetchingPreviousPage}
          isFetchEnabled={fetchEnabled}
          onEnable={() => {
            enableFetch(true);
            fetchPreviousPage().catch(console.warn);
          }}
        />
      ) : (
        <LoadingHeader
          hide={!hasPreviousPage}
          isLoading={isFetchingPreviousPage}
        />
      ),
    ScrollSeekPlaceholder: ({ height }) => <div style={{ height }} />,
  } satisfies Components;

  const handleFollowOutput = (atBottom: boolean) => {
    if (atBottom || messages.at(-1)?.Status === null) {
      return 'auto';
    }
    return false;
  };

  return (
    <>
      {!isEmpty(groupCounts) ? (
        <GroupedVirtuoso
          ref={listRef}
          id={`virtuoso-list-${listId}`}
          skipAnimationFrameInResizeObserver
          className='h-full flex-auto overflow-x-hidden bg-base-100 overscroll-none '
          overscan={4}
          atBottomStateChange={(atBottom) => {
            setAtEdge((state) => (state === 'head' ? state : atBottom ? 'tail' : null));
            if (atBottom) {
              debouncedEndReached();

              // rangeChanged and atBottomStateChange cannot be called simultaneously,
              // reset the lastPosition here instead of in handleRangeChange
              setLastPosition(0);
            }
          }}
          atTopStateChange={(atTop) => {
            setAtEdge((state) => (state === 'tail' ? state : atTop ? 'head' : null));
            if (atTop) debouncedStartReached();
          }}
          groupCounts={groupCounts}
          groupContent={(index) => <DateHeader date={groupedDates[index] ?? ''} />}
          defaultItemHeight={showHeader ? 92 : 68}
          atBottomThreshold={50}
          itemContent={renderItemContent}
          followOutput={handleFollowOutput}
          rangeChanged={handleRangeChange}
          components={components}
          firstItemIndex={firstItemIndex}
          initialTopMostItemIndex={messages.length - 1}
          scrollSeekConfiguration={{
            enter: (velocity) => Math.abs(velocity) > 650,
            exit: (velocity) => Math.abs(velocity) < 150,
          }}
        />
      ) : (
        <EmptyLayout
          label={type === 'group' ? 'conversation.group-welcome' : 'conversation.message.start-messaging'}
          className='bg-base-100 flex-1'
        />
      )}
      <ScrollToLastReadButton
        count={unreadCount}
        onClick={() => scrollTo(unreadMessagesIndex)}
      />
      <ScrollToEndButton
        isAtTailEdge={debouncedAtEdge === 'tail'}
        onClick={() => handleScrollToEdgeClick('tail')}
      />
    </>
  );
}

function groupMessagesByDate(messages: ConversationMessageTypeUnion[]): [string[], number[]] {
  const groupCounts = messages.reduce(
    (accu, message) => {
      const groupName = dayjs(message.CreateTime).format('YYYYMMDD');
      if (accu[groupName]) {
        accu[groupName]++;
      } else {
        accu[groupName] = 1;
      }

      return accu;
    },
    {} as Record<string, number>,
  );

  return sortBy(([date]) => Number(date), Object.entries(groupCounts)).reduce(
    (accu, item) => {
      accu[0].push(item[0]);
      accu[1].push(item[1]);

      return accu;
    },
    [[], []] as [string[], number[]],
  );
}
